import {
  Box,
  Container,
  CSSObject,
  Divider,
  Drawer,
  styled,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Stack,
  Grid2,
} from "@mui/material";
import { blue, orange, red, yellow } from "@mui/material/colors";
import {
  IconCircle,
  IconDashboard,
  IconMessage,
  IconBrandMessenger,
  IconHeartMinus,
  IconPlayFootball,
  IconBrandTelegram,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouteLoaderData,
} from "react-router-dom";
import { AppHeader } from "./AppHeader";
import { NavMenu, TMenuItem } from "./NavMenu";
import { APP_TITLE } from "../../constants/dashboard";
import Watch from "../../assets/images/Watch.svg";

export const drawerWidth = 250;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  boxShadow: "10px 4px 32px #4b4b7129",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: theme.spacing(7),
});

const DesktopDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, dashboardList } = useRouteLoaderData("root") as {
    isAuthenticated: boolean;
    dashboardList: { DashboardId: string; Name: string }[];
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated && location.pathname) {
      if (!localStorage.getItem("user")) {
        navigate("/auth/login");
      } else if (location.pathname === "/") {
        navigate("/device");
      } else {
        navigate(location.pathname);
      }
    } else if (location.pathname === "/") {
      navigate("/device");
    }
  }, [isAuthenticated, location.pathname]);

  const items: TMenuItem[] = [
    {
      link: "/device",
      label: "Device",
      icon: (
        <img
          src={Watch}
          style={{
            width: "25px",
            cursor: "pointer",
          }}
          alt="Watch"
        />
      ),
      children: [],
    },
    {
      link: "/report",
      label: "Report",
      icon: <IconDashboard stroke={2} />,
      children: [
        {
          link: "/report/bpm",
          title: "Heart Rate BPM",
          icon: <IconHeartMinus stroke={2} color={red[500]} />,
        },
        {
          link: "report/steps",
          title: "Steps",
          icon: <IconPlayFootball stroke={2} color={orange[500]} />,
        },
        {
          link: "report/alarm",
          title: "Message List",
          icon: <IconBrandMessenger stroke={2} color={yellow[500]} />,
        },
        {
          link: "report/gnss",
          title: "GNSS",
          icon: <IconBrandTelegram stroke={2} color={blue[500]} />,
        },
      ],
    },
    {
      link: "/message",
      label: "Send Message",
      icon: <IconMessage stroke={2} />,
      children: [],
    },
  ];

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      if (!isClosing) {
        setMobileOpen(!mobileOpen);
      }
    } else setOpen(!open);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <AppHeader open={open} onMenuClick={handleDrawerToggle} />
      <Box component="nav" sx={{ flexShrink: { sm: 0 } }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
              },
            }}
          >
            <Toolbar sx={{ backgroundColor: "#006464" }}>
              <Typography
                variant="h6"
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: "#ffffff",
                }}
              >
                {APP_TITLE.TITLE}
              </Typography>
            </Toolbar>
            <Divider />
            <NavMenu items={items} isMobile />
          </Drawer>
        ) : (
          <DesktopDrawer variant="permanent" open={open}>
            <Toolbar sx={{ p: "0!important", backgroundColor: "#006464" }}>
              <Box
                sx={{
                  maxWidth: 500,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    width: "100%",
                    paddingLeft: "18px",
                    color: "#ffffff",
                  }}
                >
                  mSafety
                </Typography>
              </Box>
            </Toolbar>
            <Divider />
            <NavMenu
              items={items}
              isMimi={!open}
              dashboardList={dashboardList}
              width={"250px"}
              minWidth={"250px"}
            />
          </DesktopDrawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          backgroundColor: "unset",
          flexGrow: 1,
          width: isMobile
            ? "100%"
            : `calc(100% - ${open ? drawerWidth + "px" : theme.spacing(7)})`,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Container sx={{ p: "12px 26px 26px" }} maxWidth={false}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
